<template>
  <v-card class="elevation-1 mt-10 mb-7">
    <v-toolbar flat>
      <v-toolbar-title>Traveler Categories</v-toolbar-title>
      <v-tooltip content-class="tooltip black" max-width="480" top>
        <template #activator="{ on, attrs }">
          <v-icon class="ml-2" small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
        </template>
        <span>
          Anything not listed here stays as-is. Within a category, leaving a field on "Default" or leaving age blank
          means the default value is kept.
        </span>
      </v-tooltip>
      <v-spacer></v-spacer>
      <v-btn color="accent" dark @click="addItem"> New Item </v-btn>
    </v-toolbar>

    <v-expansion-panels v-if="data.length" v-model="openPanels" accordion multiple flat class="px-4 pb-4">
      <v-expansion-panel v-for="(item, index) in data" :key="index" ref="panels" class="traveler-category-panel">
        <v-expansion-panel-header>
          <div class="d-flex align-center flex-grow-1">
            <span class="font-weight-medium">{{ categoryLabel(item) }}</span>
            <span class="text-caption grey--text ml-3">{{ tabSummary(item) }}</span>
            <v-chip v-if="isHidden(item)" class="ml-3" color="primary" x-small label outlined>Hidden</v-chip>
            <v-spacer></v-spacer>
            <v-btn class="mr-2" color="primary" icon small @click.stop="removeItem(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <v-row class="mx-0">
            <v-col cols="12" md="4" lg="3">
              <v-select
                :items="categories"
                v-model="item.category"
                label="Category"
                hide-details="auto"
                dense
                outlined></v-select>
            </v-col>
            <v-col cols="12" md="4" lg="5">
              <v-select
                :value="item.tabs || []"
                :items="tabs"
                :error="!!rowConflict(index)"
                :error-messages="rowConflict(index) ? [rowConflict(index)] : []"
                class="traveler-category-tabs"
                label="Tabs"
                placeholder="Default"
                persistent-placeholder
                multiple
                hide-details="auto"
                dense
                outlined
                @change="setTabs(item, $event)"></v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                :value="flagValue(item, 'visible')"
                :items="flagOptions"
                label="Visible"
                hide-details="auto"
                dense
                outlined
                @change="setFlag(item, 'visible', $event)"></v-select>
            </v-col>

            <v-col cols="6" md="3">
              <v-text-field
                v-model.number="item.ageRangeMin"
                :disabled="isHidden(item)"
                type="number"
                min="0"
                label="Min age"
                hint="Leave empty to use the default"
                persistent-hint
                dense
                outlined></v-text-field>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field
                v-model.number="item.ageRangeMax"
                :disabled="isHidden(item)"
                :error="isInvalidRange(item)"
                :error-messages="isInvalidRange(item) ? ['Min age cannot be greater than max age'] : []"
                type="number"
                min="0"
                label="Max age"
                hint="Leave empty to use the default"
                persistent-hint
                dense
                outlined></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                :value="flagValue(item, 'showAgeRange')"
                :items="flagOptions"
                :disabled="isHidden(item)"
                label="Show age range"
                hide-details="auto"
                dense
                outlined
                @change="setFlag(item, 'showAgeRange', $event)"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                :value="flagValue(item, 'showInformationText')"
                :items="flagOptions"
                :disabled="isHidden(item)"
                label="Show information text"
                hide-details="auto"
                dense
                outlined
                @change="setFlag(item, 'showInformationText', $event)"></v-select>
            </v-col>

            <v-col cols="12">
              <div class="text-caption grey--text mb-1">
                Information text. A language left empty keeps the default text for that language.
              </div>
              <v-tabs :value="localeTab(index)" @change="setLocaleTab(index, $event)" show-arrows>
                <v-tab v-for="locale in locales" :key="locale.value">
                  {{ locale.text }}
                  <v-icon v-if="hasText(item, locale.value)" class="ml-1" x-small color="greeny">
                    mdi-check-circle
                  </v-icon>
                </v-tab>
              </v-tabs>
              <v-tabs-items :value="localeTab(index)">
                <v-tab-item v-for="locale in locales" :key="locale.value">
                  <vue-editor
                    :value="textFor(item, locale.value)"
                    :disabled="isHidden(item)"
                    :editor-toolbar="informationTextToolbar"
                    class="traveler-category-editor mt-4"
                    @input="setText(item, locale.value, $event)"></vue-editor>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import { VueEditor } from "vue2-editor";
import { sanitizeHtml } from "@/util/sanitize-html";

export const TRAVELER_CATEGORIES = [
  { value: "ADULT", text: "Adult" },
  { value: "CHILD_YOUTH", text: "Child/Youth" },
  { value: "STUDENT", text: "Student" },
  { value: "RETIRED", text: "Retired" },
  { value: "SENIOR", text: "Senior" },
];

export const TRAVELER_CATEGORY_TABS = [
  { value: "tickets", text: "Single tickets" },
  { value: "travel-passes", text: "Period tickets" },
  { value: "train-vacation-cards", text: "Train vacation tickets" },
];

const labelsOf = (options) =>
  options.reduce((labels, option) => {
    labels[option.value] = option.text;
    return labels;
  }, {});

export const TRAVELER_CATEGORY_LABELS = labelsOf(TRAVELER_CATEGORIES);
const TAB_LABELS = labelsOf(TRAVELER_CATEGORY_TABS);

const BOOLEAN_FIELDS = ["visible", "showAgeRange", "showInformationText"];

const FLAG_OPTIONS = [
  { value: "default", text: "Default" },
  { value: "yes", text: "Yes" },
  { value: "no", text: "No" },
];

const toFlagKey = (value) => {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "default";
};

const fromFlagKey = (key) => {
  if (key === "yes") return true;
  if (key === "no") return false;
  return null;
};

const BLANK_ROW_FIELDS = {
  tabs: null,
  ageRangeMin: null,
  ageRangeMax: null,
  visible: null,
  showAgeRange: null,
  showInformationText: null,
};

const isEmpty = (value) => value === null || value === undefined || value === "";

const toAge = (value) => (isEmpty(value) ? null : Number(value));

const tabLabel = (tab) => TAB_LABELS[tab] || tab;

const rowTabs = (item) => (Array.isArray(item.tabs) && item.tabs.length > 0 ? item.tabs : null);

const overlappingTabs = (first, second) => {
  const firstTabs = rowTabs(first);
  const secondTabs = rowTabs(second);

  if (firstTabs === null && secondTabs === null) return [];
  if (firstTabs === null || secondTabs === null) return null;

  const shared = firstTabs.filter((tab) => secondTabs.includes(tab));

  return shared.length > 0 ? shared : null;
};

const conflictScope = (items, index) => {
  const item = items[index];

  if (!item || !item.category) return "";

  for (let previous = 0; previous < index; previous++) {
    const other = items[previous];

    if (!other || other.category !== item.category) continue;

    const overlap = overlappingTabs(other, item);

    if (overlap === null) continue;

    return overlap.length === 0 ? "the default tabs" : overlap.map(tabLabel).join(", ");
  }

  return "";
};

// The editor never gives an empty string back: clearing a language leaves "<p><br></p>" behind. Only
// the words a language carries decide whether it was written, so the markup is stripped before asking.
const hasHtmlContent = (html) =>
  typeof html === "string" &&
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;

const cleanTexts = (texts) => {
  if (!texts || typeof texts !== "object") return null;

  const cleaned = Object.entries(texts).reduce((result, [locale, text]) => {
    // Tora prints these texts as HTML, so what leaves Mango is stripped down to the tags the
    // toolbar can produce and every link is pinned to a new tab.
    if (hasHtmlContent(text)) result[locale] = sanitizeHtml(text.trim());
    return result;
  }, {});

  return Object.keys(cleaned).length > 0 ? cleaned : null;
};

export const buildTravelerCategoryPayload = (items) => {
  const rows = items || [];
  const payload = [];

  for (let index = 0; index < rows.length; index++) {
    const item = rows[index];

    if (!item || !item.category) continue;

    const ageRangeMin = toAge(item.ageRangeMin);
    const ageRangeMax = toAge(item.ageRangeMax);
    const label = TRAVELER_CATEGORY_LABELS[item.category] || item.category;

    if (ageRangeMin !== null && ageRangeMax !== null && ageRangeMin > ageRangeMax) {
      return { payload: [], error: `Min age cannot be greater than max age for category: ${label}` };
    }

    const conflict = conflictScope(rows, index);

    if (conflict) {
      return { payload: [], error: `Another item already covers ${conflict} for category: ${label}` };
    }

    const setting = { category: item.category, tabs: rowTabs(item), ageRangeMin, ageRangeMax };
    let hasOverride = ageRangeMin !== null || ageRangeMax !== null;

    for (const field of BOOLEAN_FIELDS) {
      if (typeof item[field] !== "boolean") continue;
      setting[field] = item[field];
      hasOverride = true;
    }

    const texts = cleanTexts(item.informationTextPerLanguage);
    if (texts) {
      setting.informationTextPerLanguage = texts;
      hasOverride = true;
    }

    if (hasOverride) payload.push(setting);
  }

  return { payload, error: null };
};

export default {
  name: "TravelerCategories",
  components: { VueEditor },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    locales: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    categories: TRAVELER_CATEGORIES,
    tabs: TRAVELER_CATEGORY_TABS,
    flagOptions: FLAG_OPTIONS,
    activeLocaleTab: {},
    openPanels: [],
    // An information text is a short notice, so the toolbar stops at the formatting that reads well
    // inside Tora's alert. Anything richer would only be stripped again on save.
    informationTextToolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  }),
  watch: {
    // Rows saved before these fields existed only carry a category and an age range, and Vue 2
    // cannot bind to a key that is not on the object yet, so every row gets the missing keys added
    // reactively as soon as a tenant is loaded.
    data: {
      immediate: true,
      handler(rows) {
        (rows || []).forEach((row) => this.fillMissingFields(row));
      },
    },
  },
  methods: {
    addItem() {
      const used = this.data.map((item) => item.category);
      const unused = this.categories.find((category) => !used.includes(category.value));

      this.data.push({
        category: unused ? unused.value : this.categories[0].value,
        ...BLANK_ROW_FIELDS,
        informationTextPerLanguage: {},
      });

      const index = this.data.length - 1;
      this.openPanels = [...this.openPanels, index];

      // A new row is appended to a list that often already runs past the bottom of the window,
      // so it is scrolled to once it has rendered — otherwise the tenant is left looking at the
      // button they just pressed.
      this.$nextTick(() => {
        const panel = (this.$refs.panels || [])[index];

        if (panel) panel.$el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    removeItem(index) {
      this.data.splice(index, 1);

      // The panels are open by position, so every one below the removed row moves up with it.
      this.openPanels = this.openPanels
        .filter((panel) => panel !== index)
        .map((panel) => (panel > index ? panel - 1 : panel));
    },
    fillMissingFields(row) {
      if (!row || typeof row !== "object") return;

      for (const field of Object.keys(BLANK_ROW_FIELDS)) {
        if (row[field] === undefined) this.$set(row, field, BLANK_ROW_FIELDS[field]);
      }

      if (!row.informationTextPerLanguage || typeof row.informationTextPerLanguage !== "object") {
        this.$set(row, "informationTextPerLanguage", {});
      }
    },
    isHidden(item) {
      return item.visible === false;
    },
    categoryLabel(item) {
      return TRAVELER_CATEGORY_LABELS[item.category] || item.category || "New category";
    },
    tabSummary(item) {
      const tabs = rowTabs(item);
      return tabs ? tabs.map(tabLabel).join(", ") : "Default tabs";
    },
    setTabs(item, value) {
      // Naming no tab is how a row asks for the default, and that is sent as a null tabs list.
      item.tabs = Array.isArray(value) && value.length > 0 ? value : null;
    },
    rowConflict(index) {
      const conflict = conflictScope(this.data, index);

      return conflict ? `Another item already covers ${conflict} for this category` : "";
    },
    flagValue(item, field) {
      return toFlagKey(item[field]);
    },
    setFlag(item, field, key) {
      item[field] = fromFlagKey(key);
    },
    localeTab(index) {
      return this.activeLocaleTab[index] || 0;
    },
    setLocaleTab(index, tab) {
      this.$set(this.activeLocaleTab, index, tab);
    },
    textFor(item, locale) {
      return (item.informationTextPerLanguage || {})[locale] || "";
    },
    hasText(item, locale) {
      return hasHtmlContent(this.textFor(item, locale));
    },
    setText(item, locale, value) {
      if (!item.informationTextPerLanguage) {
        this.$set(item, "informationTextPerLanguage", {});
      }
      this.$set(item.informationTextPerLanguage, locale, value);
    },
    isInvalidRange(item) {
      const ageRangeMin = toAge(item.ageRangeMin);
      const ageRangeMax = toAge(item.ageRangeMax);
      return ageRangeMin !== null && ageRangeMax !== null && ageRangeMin > ageRangeMax;
    },
  },
};
</script>

<style scoped>
.traveler-category-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 8px;
}

/* Tabs has no "Default" entry in its list, so a row that names none says "Default" through the
   placeholder instead. Vuetify blanks it out on its own once a tab is picked, so this only drops
   the muted placeholder shade to make it read like the Default of the selects next to it. */
.traveler-category-tabs.v-input ::v-deep input::placeholder {
  color: currentColor;
}

/* The editor ships a 200px body meant for article writing. An information text is a couple of
   sentences, so it is brought back to about the three rows the plain field used to offer. */
.traveler-category-editor ::v-deep .ql-editor {
  min-height: 110px;
}
</style>
